import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/ui/components/primitives/card";
import { Button } from "@/ui/components/primitives/button";
import { Input } from "@/ui/components/primitives/input";
import { ExpandableSearchInput } from "@/ui/components/primitives/expandable-search-input";
import { Textarea } from "@/ui/components/primitives/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui/components/primitives/table";
import { Badge } from "@/ui/components/primitives/badge";
import { Checkbox } from "@/ui/components/primitives/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui/components/primitives/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/ui/components/primitives/dialog";
import { Skeleton } from "@/ui/components/primitives/skeleton";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/ui/components/primitives/pagination";
import { cn } from "@/ui/components/primitives/utils";
import { PayoutDetailModal } from "@/modules/payments/components/payout-detail-modal";
import { UpdatePayoutModal } from "@/modules/payments/components/update-payout-modal";
import {
  fetchPendingPayouts,
  fetchPayoutStats,
  approvePayout,
  rejectPayout,
  bulkApprovePayouts,
  type PayoutApproval,
  type PayoutStats,
} from "@/services/api/services/admin-payouts";
import { showSuccessToast, showErrorToast } from "@/services/toast-helper";
import { RefreshCw, CheckCircle, Clock, XCircle, Loader2, ChevronLeft, ChevronRight, Edit } from "lucide-react";

function PayoutStatusBadge({ status }: { status: string }) {
  const statusLower = status.toLowerCase();
  const variants: Record<string, string> = {
    pending: "bg-warning/10 text-warning border-warning/20",
    processing: "bg-primary/10 text-primary border-primary/20",
    queued: "bg-primary/10 text-primary border-primary/20",
    success: "bg-success/10 text-success border-success/20",
    failed: "bg-destructive/10 text-destructive border-destructive/20",
    cancelled: "bg-muted text-muted-foreground border-border",
  };

  return (
    <Badge className={cn(variants[statusLower] || variants.pending)}>
      {status}
    </Badge>
  );
}

function StatCard({ title, value, icon: Icon, color }: { title: string; value: number | string; icon: any; color: string }) {
  return (
    <Card className="p-4 sm:p-6 hover:shadow-md transition-all duration-200 hover:-translate-y-1">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs sm:text-sm text-muted-foreground mb-1">{title}</p>
          <p className="text-xl sm:text-2xl lg:text-3xl font-bold truncate">
            {typeof value === "number" ? value.toLocaleString() : value}
          </p>
        </div>
        <div className={cn("p-2 sm:p-3 rounded-lg flex-shrink-0 ml-2", color)}>
          <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
        </div>
      </div>
    </Card>
  );
}

export function ApprovalsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // State from URL params
  const page = Number(searchParams.get("page") || 1);
  const limit = Number(searchParams.get("limit") || 10);
  const search = searchParams.get("search") || "";
  const sortBy = searchParams.get("sortBy") || "createdAt";
  const sortOrder = (searchParams.get("sortOrder") || "desc") as "asc" | "desc";
  const status = searchParams.get("status") || "ALL";
  const currency = searchParams.get("currency") || "ALL";
  const method = searchParams.get("method") || "ALL";
  const processingType = searchParams.get("processingType") || "ALL";

  // Component state
  const [items, setItems] = useState<PayoutApproval[]>([]);
  const [stats, setStats] = useState<PayoutStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Detail modal state
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState<PayoutApproval | null>(null);

  // Update modal state
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updatePayout, setUpdatePayout] = useState<PayoutApproval | null>(null);

  // Reject modal state
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectPayoutId, setRejectPayoutId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectLoading, setRejectLoading] = useState(false);

  // Local search input (debounced)
  const [searchInput, setSearchInput] = useState(search);

  // Caching and request management
  const [pageCache, setPageCache] = useState(new Map<string, { data: any[]; pagination: any; timestamp: number }>());
  const [backgroundRefreshEnabled, setBackgroundRefreshEnabled] = useState(false); // Disabled by default
  const [consecutiveErrors, setConsecutiveErrors] = useState(0);
  const [isNetworkDown, setIsNetworkDown] = useState(false);
  const [lastRequestTime, setLastRequestTime] = useState(0);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [requestInProgress, setRequestInProgress] = useState(false);

  // Generate cache key for current filters and page
  const generateCacheKey = useCallback((currentPage: number, currentLimit: number, currentFilters: any) => {
    return JSON.stringify({
      page: currentPage,
      limit: currentLimit,
      search: currentFilters.search,
      status: currentFilters.status,
      currency: currentFilters.currency,
      method: currentFilters.method,
      processingType: currentFilters.processingType,
      sortBy: currentFilters.sortBy,
      sortOrder: currentFilters.sortOrder,
    });
  }, []);

  // Check if cached data is still valid (15 minutes)
  const isCacheValid = useCallback((timestamp: number) => {
    return Date.now() - timestamp < 15 * 60 * 1000; // 15 minutes
  }, []);

  // Rate limit protection
  const checkRateLimit = useCallback(() => {
    const now = Date.now();
    const minRequestInterval = 2000; // 2 seconds minimum between requests

    if (now - lastRequestTime < minRequestInterval) {
      return false;
    }

    if (isRateLimited) {
      return false;
    }

    if (requestInProgress) {
      return false;
    }

    setLastRequestTime(now);
    return true;
  }, [lastRequestTime, isRateLimited, requestInProgress]);

  
  // No auto-refresh - user-controlled refresh only

  // Update URL params
  const updateParams = useCallback((updates: Record<string, string | number | null>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === "" || value === undefined) {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }
    });
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Load stats
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await fetchPayoutStats();
      setStats(data);
    } catch (e) {
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Load payouts with rate limiting protection
  const loadPayoutsSafe = useCallback(async (forceRefresh = false) => {
    // Rate limit check
    if (!checkRateLimit()) {
      return false;
    }

    setLoading(true);
    setError("");
    setRequestInProgress(true);
    setConsecutiveErrors(0);

    try {
      const cacheKey = generateCacheKey(page, limit, { search, status, currency, method, processingType, sortBy, sortOrder });

      // Check cache first (unless forcing refresh)
      if (!forceRefresh) {
        const cached = pageCache.get(cacheKey);
        if (cached && isCacheValid(cached.timestamp)) {
          setItems(cached.data);
          setTotal(cached.pagination.total);
          setTotalPages(cached.pagination.pages);
          setConsecutiveErrors(0);
          return true; // Use cached data, no API call needed
        }
      }

      // Fetch fresh data
      const res = await fetchPendingPayouts({
        page,
        limit,
        search: search || undefined,
        sortBy,
        sortOrder,
        status: status !== 'ALL' ? status : undefined,
        currency: currency !== 'ALL' ? currency : undefined,
        method: method !== 'ALL' ? method : undefined,
        processingType: processingType !== 'ALL' ? processingType : undefined,
      });

      // Update cache
      setPageCache(prev => new Map(prev).set(cacheKey, {
        data: res.data,
        pagination: res.pagination,
        timestamp: Date.now()
      }));

      setItems(res.data);
      setTotal(res.pagination.total);
      setTotalPages(res.pagination.pages);
      setConsecutiveErrors(0);
      setIsRateLimited(false);
      return true;
    } catch (e: any) {
      setConsecutiveErrors(prev => prev + 1);

      // Handle rate limiting specifically
      if (e?.code === 'RATE_LIMIT_EXCEEDED' || e?.status === 429) {
        setIsRateLimited(true);
        setError('Rate limit exceeded. Please wait before trying again.');
        showErrorToast('Rate limit exceeded. Please wait a moment before refreshing.');

        // Auto-enable after 1 minute
        setTimeout(() => {
          setIsRateLimited(false);
          setConsecutiveErrors(0);
        }, 60000);
        return false;
      }

      // Show error but don't spam with retries
      if (setConsecutiveErrors(prev => prev + 1) <= 2) { // Only show toast for first 2 errors
        setError(e?.message || "Failed to load payouts");
        showErrorToast(e?.message || "Failed to load pending payouts");
      }

      return false;
    } finally {
      setLoading(false);
      setRequestInProgress(false);
    }
  }, [checkRateLimit, page, limit, search, sortBy, sortOrder, status, currency, method, processingType, pageCache, generateCacheKey, isCacheValid, fetchPendingPayouts]);

  // Wrapper function to maintain compatibility
  const loadPayouts = useCallback((forceRefresh = false) => {
    return loadPayoutsSafe(forceRefresh);
  }, [loadPayoutsSafe]);

  useEffect(() => {
    loadPayouts();
    loadStats();
  }, [loadPayouts, loadStats]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== search) {
        updateParams({ search: searchInput || null, page: 1 });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput, search, updateParams]);

  // Handle single approve from table
  const handleQuickApprove = async (id: string) => {
    try {
      await approvePayout(id);
      showSuccessToast("Payout approved");
      loadPayouts();
      loadStats();
    } catch (e: any) {
      showErrorToast(e?.detail?.error?.message || "Failed to approve");
    }
  };

  // Handle reject click - opens modal
  const handleRejectClick = (id: string) => {
    setRejectPayoutId(id);
    setRejectReason("");
    setRejectModalOpen(true);
  };

  // Handle reject confirmation
  const handleRejectConfirm = async () => {
    if (!rejectPayoutId || !rejectReason.trim()) {
      showErrorToast("Please provide a reason for rejection");
      return;
    }
    setRejectLoading(true);
    try {
      await rejectPayout(rejectPayoutId, rejectReason);
      showSuccessToast("Payout rejected");
      setRejectModalOpen(false);
      setRejectPayoutId(null);
      setRejectReason("");
      loadPayouts();
      loadStats();
    } catch (e: any) {
      showErrorToast(e?.detail?.error?.message || "Failed to reject");
    } finally {
      setRejectLoading(false);
    }
  };

  // Handle bulk approve
  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    setBulkLoading(true);
    try {
      const res = await bulkApprovePayouts(selectedIds);
      showSuccessToast(`Approved ${res.data.approved} of ${res.data.total} payouts`);
      setSelectedIds([]);
      loadPayouts();
      loadStats();
    } catch (e: any) {
      showErrorToast(e?.detail?.error?.message || "Bulk approve failed");
    } finally {
      setBulkLoading(false);
    }
  };

  // Handle row click to open detail
  const handleRowClick = (payout: PayoutApproval) => {
    setSelectedPayout(payout);
    setDetailOpen(true);
  };

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(items.map((i) => i.id));
    } else {
      setSelectedIds([]);
    }
  };

  // Handle select one
  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((i) => i !== id));
    }
  };

  const allSelected = items.length > 0 && selectedIds.length === items.length;

  // Generate page numbers for pagination
  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const leftBound = Math.max(1, page - 2);
      const rightBound = Math.min(totalPages, page + 2);

      if (leftBound > 1) {
        pages.push(1);
        if (leftBound > 2) pages.push('...');
      }

      for (let i = leftBound; i <= rightBound; i++) {
        pages.push(i);
      }

      if (rightBound < totalPages) {
        if (rightBound < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Skeleton loading component for table rows
  const TableSkeleton = () => (
    <>
      {Array.from({ length: limit }, (_, i) => (
        <TableRow key={`skeleton-${i}`}>
          <TableCell><Skeleton className="h-4 w-4" /></TableCell>
          <TableCell><Skeleton className="h-4 w-16" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
          <TableCell><Skeleton className="h-4 w-12" /></TableCell>
          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-20 w-full flex gap-2" /></TableCell>
        </TableRow>
      ))}
    </>
  );

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden min-w-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Payout Approvals</h1>
          <p className="text-muted-foreground mt-1">Review and authorize merchant payout requests</p>
        </div>
        <Button
          variant="default"
          size="sm"
          onClick={() => { loadPayouts(true); loadStats(); }}
          disabled={loading || isRateLimited}
          className={cn(
            "min-w-[100px]",
            isRateLimited && "bg-orange-500 hover:bg-orange-600"
          )}
        >
          <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
          {loading ? "Loading..." : isRateLimited ? "Rate Limited" : "Refresh"}
        </Button>
      </div>

      {/* Rate Limit Warning */}
      {isRateLimited && (
        <div className="bg-orange-50 border border-orange-200 text-orange-800 p-3 rounded-md flex items-center gap-2">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span className="text-sm">
            Rate limit active. Please wait before making more requests.
          </span>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <StatCard
          title="Pending"
          value={statsLoading ? "..." : stats?.pending || 0}
          icon={Clock}
          color="bg-warning/10 text-warning"
        />
        <StatCard
          title="Processing"
          value={statsLoading ? "..." : stats?.processing || 0}
          icon={Loader2}
          color="bg-primary/10 text-primary"
        />
        <StatCard
          title="Success"
          value={statsLoading ? "..." : stats?.success || 0}
          icon={CheckCircle}
          color="bg-success/10 text-success"
        />
        <StatCard
          title="Failed"
          value={statsLoading ? "..." : stats?.failed || 0}
          icon={XCircle}
          color="bg-destructive/10 text-destructive"
        />
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col gap-4">
          {/* First Row: Search, Status, and Sort */}
          <div className="flex flex-col sm:flex-row gap-4">
            <ExpandableSearchInput
              placeholder="Search by purpose, reference, notes..."
              value={searchInput}
              onChange={setSearchInput}
            />
            <Select value={status} onValueChange={(v) => updateParams({ status: v, page: 1 })}>
              <SelectTrigger className="w-full sm:w-44">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Status</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="QUEUED">Queued</SelectItem>
                <SelectItem value="PROCESSING">Processing</SelectItem>
                <SelectItem value="SUCCESS">Success</SelectItem>
                <SelectItem value="FAILED">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={(v) => updateParams({ sortBy: v, page: 1 })}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="createdAt">Date</SelectItem>
                <SelectItem value="amount">Amount</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortOrder} onValueChange={(v) => updateParams({ sortOrder: v, page: 1 })}>
              <SelectTrigger className="w-full sm:w-32">
                <SelectValue placeholder="Order" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">Newest</SelectItem>
                <SelectItem value="asc">Oldest</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Second Row: Currency, Method, Processing Type */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={currency} onValueChange={(v) => updateParams({ currency: v, page: 1 })}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Currency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Currencies</SelectItem>
                <SelectItem value="INR">INR</SelectItem>
                <SelectItem value="USDT">USDT</SelectItem>
              </SelectContent>
            </Select>

            <Select value={method} onValueChange={(v) => updateParams({ method: v, page: 1 })}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Methods</SelectItem>
                <SelectItem value="BANK_ACCOUNT">Bank Transfer</SelectItem>
                <SelectItem value="UPI">UPI</SelectItem>
                <SelectItem value="WALLET">Wallet</SelectItem>
              </SelectContent>
            </Select>

            <Select value={processingType} onValueChange={(v) => updateParams({ processingType: v, page: 1 })}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Processing" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Types</SelectItem>
                <SelectItem value="MANUAL">Manual (USDT)</SelectItem>
                <SelectItem value="AUTOMATIC">Automatic (PSP)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
          <span className="text-sm font-medium">{selectedIds.length} selected</span>
          <Button size="sm" onClick={handleBulkApprove} disabled={bulkLoading} className="bg-success hover:bg-success/90">
            {bulkLoading ? "Approving..." : "Approve Selected"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setSelectedIds([])}>
            Clear
          </Button>
        </div>
      )}

      {/* Desktop Table */}
      <Card className="hidden md:block">
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all"
                  />
                </TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Merchant</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead>Purpose</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableSkeleton />
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12 text-destructive">{error}</TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                    No pending payouts
                  </TableCell>
                </TableRow>
              ) : (
                items.map((p) => (
                  <TableRow
                    key={p.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleRowClick(p)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.includes(p.id)}
                        onCheckedChange={(checked) => handleSelectOne(p.id, !!checked)}
                        aria-label={`Select ${p.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{p.id.slice(-12)}</TableCell>
                    <TableCell className="font-medium">{p.merchantName || p.merchant?.name || 'Unknown Merchant'}</TableCell>
                    <TableCell className="font-semibold">
                      {p.currency === "USDT" ? "$" : "₹"}{p.amount.toLocaleString()}
                    </TableCell>
                    <TableCell>{p.currency}</TableCell>
                    <TableCell className="max-w-[150px] truncate">{p.purpose}</TableCell>
                    <TableCell><PayoutStatusBadge status={p.status} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(p.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setUpdatePayout(p);
                            setUpdateModalOpen(true);
                          }}
                          title="Edit payout"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleQuickApprove(p.id)}
                          className="text-success border-success hover:bg-success/10"
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRejectClick(p.id)}
                          className="text-destructive border-destructive hover:bg-destructive/10"
                        >
                          Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Enhanced Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between p-4 border-t gap-4">
          <p className="text-sm text-muted-foreground">
            Showing {items.length} of {total} pending payouts
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-4">
            {/* Page Size Selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Show:</span>
              <Select value={String(limit)} onValueChange={(v) => updateParams({ limit: v, page: 1 })}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Pagination Component */}
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => updateParams({ page: 1 })}
                    disabled={page <= 1 || loading}
                  >
                    First
                  </Button>
                </PaginationItem>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => updateParams({ page: page - 1 })}
                    className={cn(page <= 1 && "opacity-50 cursor-not-allowed")}
                  />
                </PaginationItem>

                {generatePageNumbers().map((pageNum, index) => (
                  <PaginationItem key={`page-${index}`}>
                    {pageNum === '...' ? (
                      <PaginationEllipsis />
                    ) : (
                      <PaginationLink
                        href="#"
                        isActive={pageNum === page}
                        onClick={(e) => {
                          e.preventDefault();
                          updateParams({ page: pageNum });
                        }}
                      >
                        {pageNum}
                      </PaginationLink>
                    )}
                  </PaginationItem>
                ))}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => updateParams({ page: page + 1 })}
                    className={cn(page >= totalPages && "opacity-50 cursor-not-allowed")}
                  />
                </PaginationItem>
                <PaginationItem>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => updateParams({ page: totalPages })}
                    disabled={page >= totalPages || loading}
                  >
                    Last
                  </Button>
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      </Card>

      {/* Mobile Card List */}
      <Card className="md:hidden p-4">
        <div className="space-y-3">
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: limit }, (_, i) => (
                <div key={`mobile-skeleton-${i}`} className="p-4 border rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                  <div className="flex justify-between">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <div className="flex justify-between">
                    <Skeleton className="h-4 w-12" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Skeleton className="h-8 w-16" />
                    <Skeleton className="h-8 w-16" />
                    <Skeleton className="h-8 w-16" />
                    <Skeleton className="h-8 w-16" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center text-destructive py-12">{error}</div>
          ) : items.length === 0 ? (
            <div className="text-center text-muted-foreground py-12">No pending payouts</div>
          ) : (
            items.map((p) => (
              <div
                key={p.id}
                className="p-4 border rounded-lg space-y-2 cursor-pointer hover:bg-muted/50"
                onClick={() => handleRowClick(p)}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs">{p.id.slice(-12)}</span>
                  <PayoutStatusBadge status={p.status} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Merchant</span>
                  <span className="text-sm font-medium">{p.merchantName || p.merchant?.name || 'Unknown Merchant'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Amount</span>
                  <span className="font-semibold">
                    {p.currency === "USDT" ? "$" : "₹"}{p.amount.toLocaleString()} {p.currency}
                  </span>
                </div>
                <div className="flex items-center justify-end gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setUpdatePayout(p);
                      setUpdateModalOpen(true);
                    }}
                    title="Edit payout"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleQuickApprove(p.id)}
                    className="bg-success hover:bg-success/90"
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRejectClick(p.id)}
                    className="border-destructive text-destructive hover:bg-destructive/10"
                  >
                    Reject
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleRowClick(p)}>
                    Details
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Mobile Pagination */}
        {items.length > 0 && (
          <div className="flex flex-col items-center gap-3 pt-4 border-t mt-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages} • {items.length} of {total} items
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Select value={String(limit)} onValueChange={(v) => updateParams({ limit: v, page: 1 })}>
                <SelectTrigger className="w-16">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => updateParams({ page: page - 1 })}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages || loading}
                onClick={() => updateParams({ page: page + 1 })}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      <PayoutDetailModal
        open={detailOpen}
        onOpenChange={setDetailOpen}
        payout={selectedPayout}
        onActionComplete={() => {
          loadPayouts();
          loadStats();
        }}
      />

      {/* Update Payout Modal */}
      <UpdatePayoutModal
        open={updateModalOpen}
        onOpenChange={setUpdateModalOpen}
        payout={updatePayout}
        onUpdateComplete={() => {
          loadPayouts();
          loadStats();
          setUpdatePayout(null);
        }}
      />

      {/* Reject Confirmation Dialog */}
      <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Payout</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this payout. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="reject-reason" className="text-sm font-medium">
                Rejection Reason *
              </label>
              <Textarea
                id="reject-reason"
                placeholder="Enter the reason for rejection..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
                className="resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectModalOpen(false)}
              disabled={rejectLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRejectConfirm}
              disabled={rejectLoading || !rejectReason.trim()}
            >
              {rejectLoading ? "Rejecting..." : "Confirm Rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
